import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, ShieldAlert } from "lucide-react";
import { useZone } from "@/components/session-provider";
import { SecurityEvidence } from "@/components/reports/security-evidence";
import { formatDateTime } from "@/lib/time";
import {
  auditLinkFor,
  DEFAULT_SECURITY_DAYS,
  fetchSecurityReport,
  SECURITY_PERIODS,
  type SecurityActor,
  type SecurityReport,
  SIGNAL_LABELS,
} from "@/lib/reports-security";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/reports/security")({
  component: SecurityReportPage,
});

/**
 * **Access review.**
 *
 * Who has been reading the record, who has been refused, and which accounts are worth asking
 * about — read from the audit ledger and from nothing else.
 *
 * ⚠️ **Gated on `audit.read` and on no reporting tier**, and the server is what enforces it. This
 * payload is about who has been looking at whom, which is the question that permission exists to
 * answer and the reason it is held by Owner and Auditor rather than by every administrator.
 * Borrowing the roster's fence would have published every colleague's access pattern to every
 * Team Admin (D-75).
 *
 * ⚠️ **It reports measures and never a verdict.** No score, no rank, no risk grade — the server
 * record refuses those names and `SecurityReportTests` asserts it. Rows are ordered by one named
 * measure with the basis printed beside it (D-55), and every signal states the rule it applied so
 * a reader can check it by hand in front of the person it fired about.
 *
 * ⚠️ **Nothing here measures work.** No handle time, no requests resolved, no response speed —
 * that is the distinction that lets this screen exist beside D-56, D-118 and D-119 rather than
 * against them. An access review with a productivity column is a performance review with a
 * stronger gate and a weaker challenge.
 */
function SecurityReportPage() {
  const zone = useZone();

  const [days, setDays] = useState(DEFAULT_SECURITY_DAYS);
  const [report, setReport] = useState<SecurityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetchSecurityReport(days)
      .then((result) => {
        if (controller.signal.aborted) return;
        setReport(result);
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;

        // ⚠️ A failure renders AS a failure and never as an empty report. Collapsing "this could
        // not be loaded" into "nothing happened" is D-122's defect, and on this screen the
        // reassuring reading of an empty result is the dangerous one.
        setError(cause instanceof Error ? cause.message : "That report could not be loaded.");
        setReport(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [days]);

  const flagged = useMemo(() => report?.actors.filter((a) => a.signals.length > 0) ?? [], [report]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 sm:p-6">
      <header className="space-y-3">
        <Link
          to="/reports"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Reports
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Access review</h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Who has been reading the record, whose access is being refused, and which accounts are
              worth asking about. Read from the audit log.
            </p>
          </div>

          <div className="flex flex-wrap gap-1" role="group" aria-label="Period">
            {SECURITY_PERIODS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDays(option)}
                aria-pressed={days === option}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                  days === option
                    ? "border-foreground/20 bg-foreground text-background"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {option === 1 ? "24 hours" : `${option} days`}
              </button>
            ))}
          </div>
        </div>
      </header>

      {error !== null && (
        <p className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      )}

      {loading && report === null && (
        <p className="text-sm text-muted-foreground">Reading the ledger…</p>
      )}

      {report !== null && (
        <>
          {/*
            ⚠️ The scope note. Two administrators can legitimately read different totals here on a
            two-tenant deployment, because this report is scoped to the caller's tenant and the
            audit screen is not. Saying so is the same courtesy D-52 pays on the analytics page.
          */}
          <p className="text-xs text-muted-foreground">
            {report.totals.events.toLocaleString()} events by{" "}
            {report.totals.actors.toLocaleString()}{" "}
            {report.totals.actors === 1 ? "account" : "accounts"}, from{" "}
            {formatDateTime(report.window.fromUtc, zone)} to{" "}
            {formatDateTime(report.window.toUtc, zone)}. Times are shown in{" "}
            {report.timeZoneId.replace(/_/g, " ")}.
          </p>

          <Totals report={report} />

          <Clock report={report} />

          <Signals report={report} days={days} />

          <Accounts report={report} flagged={flagged} days={days} />
        </>
      )}
    </div>
  );
}

/** What happened, by kind — the denominator every count below is read against (D-53). */
function Totals({ report }: { report: SecurityReport }) {
  const tiles: { label: string; value: number; hint?: string }[] = [
    { label: "Records read", value: report.totals.reads },
    { label: "Changes", value: report.totals.writes },
    { label: "Refusals", value: report.totals.refusals, hint: "Authorization checks that said no" },
    { label: "Bulk exports", value: report.totals.exports },
    { label: "Access changes", value: report.totals.permissionChanges },
    { label: "High severity", value: report.totals.highSeverity },
  ];

  return (
    <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {tiles.map((tile) => (
        <div key={tile.label} className="rounded-lg border border-border bg-card p-3">
          <p className="text-lg font-semibold tabular-nums">{tile.value.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">{tile.label}</p>
          {tile.hint !== undefined && (
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground/80">{tile.hint}</p>
          )}
        </div>
      ))}
    </section>
  );
}

/**
 * Reads folded onto the clock, for the whole tenant.
 *
 * ⚠️ **No person axis** (D-135). A 7×24 grid of a named colleague's activity is one step from a
 * grid of when they are at their desk — the artefact D-56 calls surveillance and D-118 and D-119
 * each refused a version of. This shows a three-in-the-morning spike without characterising
 * anybody's day; who was behind it is answered by opening an account.
 *
 * ⚠️ Hours outside the working day are **marked, not hidden**, so the reader can see the shape of
 * the whole day and judge the marked ones against it.
 */
function Clock({ report }: { report: SecurityReport }) {
  const peak = report.byHour.reduce((most, hour) => Math.max(most, hour.reads), 0);

  if (peak === 0) return null;

  return (
    <section className="group/card space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-1 text-sm font-semibold">
          When records were read
          <InfoTip id="sec-hours" />
        </h2>
        <p className="text-xs text-muted-foreground">
          Every account together, by hour in {report.timeZoneId.replace(/_/g, " ")}.
          {report.businessHoursKnown
            ? " Shaded hours are outside the working week."
            : " No working week is configured, so none are marked."}
        </p>
      </div>

      <div className="flex items-end gap-px overflow-x-auto rounded-lg border border-border bg-card p-3">
        {report.byHour.map((hour) => (
          <div key={hour.hour} className="flex min-w-[1.4rem] flex-1 flex-col items-center gap-1">
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {hour.reads > 0 ? hour.reads.toLocaleString() : ""}
            </span>
            <div
              className={cn(
                "w-full rounded-sm",
                hour.withinWorkingHours === false ? "bg-amber-500/70" : "bg-foreground/30",
              )}
              style={{ height: `${Math.max(2, Math.round((hour.reads / peak) * 56))}px` }}
              aria-hidden
            />
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {String(hour.hour).padStart(2, "0")}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Each signal, its question, and the rule it applied.
 *
 * ⚠️ **The rule comes from the server and is never restated here.** Copy that describes a control
 * is part of the control (D-137), and a threshold typed into a component is a second description
 * of one — which is how a screen ends up telling a reader something the code stopped doing.
 */
function Signals({ report, days }: { report: SecurityReport; days: number }) {
  return (
    <section className="group/card space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-1 text-sm font-semibold">
          What stood out
          <InfoTip id="sec-standouts" />
        </h2>
        <p className="text-xs text-muted-foreground">
          Every line is a rule you can check. A rule that did not fire says zero rather than
          disappearing.
        </p>
      </div>

      <ul className="divide-y divide-border rounded-lg border border-border bg-card">
        {report.signals.map((signal) => (
          <li key={signal.signal} className="flex flex-wrap items-start gap-3 p-3">
            <span
              className={cn(
                "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
                !signal.applicable
                  ? "bg-muted text-muted-foreground"
                  : signal.actors > 0
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {/*
                ⚠️ An inapplicable signal reads "—", never "0". D-53: a figure nobody could
                measure is a gap, and a zero here would be reassurance the data cannot support.
              */}
              {signal.applicable ? signal.actors : "—"}
            </span>

            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-medium">{signal.question}</p>
              <p className="text-xs text-muted-foreground">{signal.rule}</p>
              {!signal.applicable && signal.notApplicableReason !== null && (
                <p className="text-xs text-muted-foreground/80">{signal.notApplicableReason}</p>
              )}

              {/*
                ⚠️ Who fired it, and the figure that tripped it — composed server-side from the
                same constants the rule states, never assembled here (D-137). A signal that says
                only "3 accounts" makes the reader go and find them; this is the finding.

                ⚠️ Ordered by NAME, as the server sends it. Sorting these by magnitude would
                publish an ordering that reads as severity — a ranking of colleagues assembled by
                the product rather than a list of findings (D-55).
              */}
              {signal.hits.length > 0 && (
                <ul className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
                  {signal.hits.map((hit) => (
                    <li key={hit.actorUserId} className="text-xs">
                      <Link
                        {...auditLinkFor(hit.actorUserId, days)}
                        className="font-medium hover:underline"
                      >
                        {hit.actorName}
                      </Link>{" "}
                      <span className="text-muted-foreground">— {hit.finding}</span>
                    </li>
                  ))}
                </ul>
              )}

              {signal.hits.length < signal.actors && (
                <p className="pt-0.5 text-[11px] text-muted-foreground">
                  and {(signal.actors - signal.hits.length).toLocaleString()} more.
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The accounts, ordered by one named measure.
 *
 * ⚠️ **Ordered by records read, and the heading says so** (D-55). Not by how many signals fired:
 * ordering by that would publish the composite this report refuses to compute, and the reader
 * would stop reading the rules.
 */
function Accounts({
  report,
  flagged,
  days,
}: {
  report: SecurityReport;
  flagged: SecurityActor[];
  days: number;
}) {
  const capped = report.actorsTotal > report.actors.length;

  /**
   * Which account's evidence is open.
   *
   * ⚠️ **One at a time**, so opening a row is one deliberate act with one audit entry behind it
   * rather than a page that quietly fetches everybody's detail as somebody clicks around.
   */
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="group/card space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-1 text-sm font-semibold">
          Accounts <InfoTip id="sec-accounts" />{" "}
          <span className="font-normal text-muted-foreground">
            · {flagged.length} of {report.actorsTotal} flagged
          </span>
        </h2>

        <p className="text-xs text-muted-foreground">
          Ordered by records opened. The middle of this group opened{" "}
          {report.cohort.medianRecordsRead.toLocaleString()}.
        </p>
      </div>

      {report.actors.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nothing was recorded in this period.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[52rem] text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="p-2 text-left font-medium">
                  Account
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Records read
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Acted on
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Out of hours
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Refused
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Exports
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Addresses
                </th>
                <th scope="col" className="p-2 text-left font-medium">
                  Signals
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {report.actors.map((actor) => (
                <Fragment key={actor.actorUserId}>
                  <tr className={cn(actor.signals.length > 0 && "bg-amber-500/[0.04]")}>
                    <td className="p-2">
                      {/*
                      ⚠️ Opening a row fetches that account's evidence — a deliberate act, and one
                      the server audits as its own. Reading the figures above discloses nobody's
                      detail (D-119, D-67).

                      ⚠️ The link beside it goes to the EXISTING audit log with this account and
                      window applied, not to a second reader over the same table. One definition of
                      "read the ledger"; and it gives D-79's actorUserId filter its caller.
                    */}
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() =>
                            setOpen((current) =>
                              current === actor.actorUserId ? null : actor.actorUserId,
                            )
                          }
                          aria-expanded={open === actor.actorUserId}
                          className="inline-flex items-center gap-1 text-left font-medium hover:underline"
                        >
                          {open === actor.actorUserId ? (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          )}
                          {actor.actorName}
                        </button>

                        <Link
                          {...auditLinkFor(actor.actorUserId, days)}
                          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                        >
                          log
                        </Link>
                      </div>
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {actor.recordsRead.toLocaleString()}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {actor.recordsActedOn.toLocaleString()}
                    </td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">
                      {/* Null is "not measured" — the working week is not configured. Never 0. */}
                      {actor.outsideHoursReads === null
                        ? "—"
                        : actor.outsideHoursReads.toLocaleString()}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {actor.refusals.toLocaleString()}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {actor.exports.toLocaleString()}
                    </td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">
                      {actor.sourceAddresses.toLocaleString()}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {actor.signals.map((signal) => (
                          <span
                            key={signal}
                            className="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                          >
                            <ShieldAlert className="h-3 w-3" aria-hidden />
                            {SIGNAL_LABELS[signal]}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>

                  {open === actor.actorUserId && (
                    <tr>
                      <td colSpan={8} className="p-0">
                        <SecurityEvidence
                          actorUserId={actor.actorUserId}
                          days={days}
                          zone={report.timeZoneId}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {capped && (
        <p className="text-xs text-muted-foreground">
          Showing {report.actors.length} of {report.actorsTotal} accounts. Every flagged account is
          listed; the rest are ordered by records opened.
        </p>
      )}

      {!report.businessHoursKnown && (
        <p className="text-xs text-muted-foreground">
          Out-of-hours reads are not measured because no working week is configured in Settings.
        </p>
      )}
    </section>
  );
}
